import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BisacCode } from "reedy-data/api";
import { BisacCodesBrowser } from "components/BisacCodes/BisacCodesBrowser";
import { Button } from "components/button";
import { SelectToggleIcon } from "components/SelectToggleIcon";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "components/ui/dialog";
import { toast } from "utils/toast";
import { AddBisacCodesSelection } from "./AddBisacCodesSelection";

export interface AddBisacCodesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Returns how many codes were new to the final list. */
  onAdd: (codes: BisacCode[]) => number;
}

// Lives inside DialogContent, so closing the dialog resets the search and selection.
const AddBisacCodesModalContent = ({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: AddBisacCodesModalProps["onAdd"];
}) => {
  const { t } = useTranslation(["productDetails", "general"]);
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [selected, setSelected] = useState<BisacCode[]>([]);

  const isSelected = (code: BisacCode) =>
    selected.some((item) => item.code === code.code);

  const toggleSelected = (code: BisacCode) =>
    setSelected((current) =>
      isSelected(code)
        ? current.filter((item) => item.code !== code.code)
        : [...current, code],
    );

  const confirm = () => {
    const addedCount = onAdd(selected);
    if (addedCount === 0) {
      toast.info(
        t("productDetails:bisac.addModal.alreadyFinal", {
          count: selected.length,
        }),
      );
    } else {
      toast.success(
        t("productDetails:bisac.addModal.success", { count: addedCount }),
      );
    }
    onClose();
  };

  return (
    <>
      <DialogHeader className="items-start space-y-0 my-0 mb-8 shrink-0">
        <DialogTitle className="pb-1">
          {t("productDetails:bisac.addModal.title")}
        </DialogTitle>
        <DialogDescription className="text-md text-secondary-400 font-light">
          {t("productDetails:bisac.addModal.description")}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col flex-1 min-h-0">
        <BisacCodesBrowser
          submittedQuery={submittedQuery}
          onSubmittedQueryChange={setSubmittedQuery}
          searchBarEnd={
            <AddBisacCodesSelection
              selected={selected}
              onRemove={toggleSelected}
            />
          }
          renderRowAction={(code) => (
            <SelectToggleIcon
              id={code.code}
              selected={isSelected(code)}
              onClick={() => toggleSelected(code)}
              size="small"
              className="text-primary-800"
            />
          )}
        />
      </div>

      <DialogFooter className="justify-end space-x-2 shrink-0">
        <DialogClose asChild>
          <Button
            variant="tertiary"
            size="small"
            label={t("general:buttons.cancel")}
          />
        </DialogClose>
        <Button
          variant="primary"
          size="small"
          label={t("productDetails:bisac.addModal.add")}
          onClick={confirm}
          disabled={selected.length === 0}
          icon="plus"
          counter={selected.length}
          counterTestId="selection-count-badge"
        />
      </DialogFooter>
    </>
  );
};

export const AddBisacCodesModal = ({
  open,
  onOpenChange,
  onAdd,
}: AddBisacCodesModalProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogOverlay className="backdrop-blur-sm bg-black/10" />
    <DialogContent
      className="min-w-[95vw] max-w-[95vw] h-[95vh] flex flex-col"
      data-testid="add-bisac-codes-modal"
    >
      <AddBisacCodesModalContent
        onClose={() => onOpenChange(false)}
        onAdd={onAdd}
      />
    </DialogContent>
  </Dialog>
);
